import { useEffect, useRef, useState, type FormEvent } from "react";
import { Brand } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { classifyAuthError, codeLength, resendCode, sendCode, submitCode, type CodeFlow } from "@/lib/auth";

type Problem = { kind: "code" | "limit" | "unknown"; message: string };

const MESSAGES: Record<Problem["kind"], string> = {
  code: "That code did not work. It may be mistyped or expired.",
  limit: "Fusion Cloud cannot send more codes right now; it sends at most 50 code emails a day. Try again later.",
  unknown: "Something went wrong. Try again in a moment.",
};

function problemFrom(error: unknown): Problem {
  const kind = classifyAuthError(error);
  return { kind, message: MESSAGES[kind] };
}

/** The only page before sign-in: lakehouse messaging, then the email step and the code step. */
export function SignInPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [flow, setFlow] = useState<CodeFlow>("sign-up");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<"sending" | "verifying" | undefined>();
  const [problem, setProblem] = useState<Problem | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const codeInput = useRef<HTMLInputElement>(null);
  const digits = codeLength(flow);

  useEffect(() => {
    if (step === "code") codeInput.current?.focus();
  }, [step]);

  async function onSubmitEmail(event: FormEvent) {
    event.preventDefault();
    const address = email.trim().toLowerCase();
    if (!address) return;
    setBusy("sending");
    setProblem(undefined);
    try {
      setFlow(await sendCode(address));
      setEmail(address);
      setCode("");
      setNotice(undefined);
      setStep("code");
    } catch (error) {
      setProblem(problemFrom(error));
    } finally {
      setBusy(undefined);
    }
  }

  async function verify(value: string) {
    if (busy || value.length !== digits) return;
    setBusy("verifying");
    setProblem(undefined);
    setNotice(undefined);
    try {
      const result = await submitCode(email, flow, value);
      if (result.signedIn) {
        onSignedIn();
        return;
      }
      setFlow(result.flow);
      setCode("");
      setNotice(`Your email address is confirmed. We sent a sign-in code to ${email}.`);
    } catch (error) {
      setProblem(problemFrom(error));
    } finally {
      setBusy(undefined);
    }
  }

  async function onSendNewCode() {
    setBusy("sending");
    setProblem(undefined);
    try {
      setFlow(await resendCode(email, flow));
      setCode("");
      setNotice(`We sent a new code to ${email}.`);
      codeInput.current?.focus();
    } catch (error) {
      setProblem(problemFrom(error));
    } finally {
      setBusy(undefined);
    }
  }

  function onUseAnotherEmail() {
    setStep("email");
    setCode("");
    setProblem(undefined);
    setNotice(undefined);
  }

  return (
    <div data-theme="dark" className="flex min-h-svh flex-col bg-linear-[165deg] from-navy-850 via-navy-900 via-55% to-navy-950 text-ink">
      <header className="flex h-[70px] items-center px-[max(20px,4vw)]">
        <Brand />
      </header>

      <main className="mx-auto box-border flex w-full max-w-[1320px] grow items-center px-[max(20px,4vw)] py-[60px]">
        <section className="flex w-full min-w-0 max-w-[760px] flex-col gap-6">
          <span className="eyebrow text-green-500">Managed Exasol for the lakehouse</span>
          <h1 className="m-0 font-display text-[40px] leading-[1.05] font-extrabold tracking-[-0.015em] text-balance sm:text-[64px]">
            Query your lakehouse where it lives
          </h1>
          <p className="m-0 max-w-[34em] text-lg leading-[1.6] text-ink-muted">
            Point Exasol at your Iceberg, Delta Lake, and Parquet tables on S3 and run fast SQL on them in place. Nothing to
            copy, nothing to load.
          </p>

          {step === "email" ? (
            <form onSubmit={onSubmitEmail} className="flex max-w-[480px] flex-col gap-2 pt-2">
              <Label htmlFor="email">Email address</Label>
              <div className="flex flex-wrap gap-3">
                <Input
                  id="email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  autoFocus
                  placeholder="you@company.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="flex-[1_1_240px]"
                  aria-describedby="email-hint"
                />
                <Button type="submit" size="lg" disabled={busy === "sending"}>
                  {busy === "sending" ? "Sending code…" : "Continue"}
                </Button>
              </div>
              {problem ? (
                <p role="alert" className="m-0 text-sm leading-normal text-ink">
                  {problem.message}
                </p>
              ) : null}
              <span id="email-hint" className="text-sm leading-normal text-ink-muted">
                We email you a code. New here? Your account is created when you enter it.
              </span>
            </form>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void verify(code);
              }}
              className="flex max-w-[480px] flex-col gap-2 pt-2"
            >
              <Label htmlFor="code">Code sent to {email}</Label>
              <div className="flex flex-wrap gap-3">
                <Input
                  ref={codeInput}
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern={`[0-9]{${digits}}`}
                  maxLength={digits}
                  placeholder={"0".repeat(digits)}
                  value={code}
                  aria-invalid={problem?.kind === "code" || undefined}
                  aria-describedby="code-status"
                  onChange={(event) => {
                    const value = event.target.value.replace(/\D/g, "").slice(0, digits);
                    setCode(value);
                    if (value.length === digits) void verify(value);
                  }}
                  className="flex-[1_1_180px] font-mono text-xl font-medium tracking-[.3em]"
                />
                <Button type="submit" size="lg" disabled={busy !== undefined || code.length !== digits}>
                  {busy === "verifying" ? "Signing in…" : "Sign in"}
                </Button>
              </div>
              <div id="code-status" aria-live="polite" className="flex flex-col gap-1">
                {problem ? (
                  <p role="alert" className="m-0 flex flex-wrap items-center gap-x-3 text-sm leading-normal text-ink">
                    <span>{problem.message}</span>
                    {problem.kind === "code" ? (
                      <Button type="button" variant="ghost" size="inline" onClick={onSendNewCode} disabled={busy !== undefined}>
                        Send a new code
                      </Button>
                    ) : null}
                  </p>
                ) : null}
                {notice ? <p className="m-0 text-sm leading-normal text-ink-muted">{notice}</p> : null}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm leading-normal text-ink-muted">
                <span>The code arrives within a minute.</span>
                <Button type="button" variant="ghost" size="inline" onClick={onUseAnotherEmail}>
                  Use another email
                </Button>
              </div>
            </form>
          )}
        </section>
      </main>

      <footer className="border-t border-line px-[max(20px,4vw)] py-5 text-sm leading-normal text-ink-muted">
        Internal prototype in eu-central-1. Anyone with an email address can sign in.
      </footer>
    </div>
  );
}
